import type { PGDExclusionCheckNode } from "../types";
import type { PreSupplyCheck } from "../types/pgd.types";

export function getPgdExclusionCheckPgdId(
  node: PGDExclusionCheckNode | null | undefined,
  selectedPgdId: string | null,
): string | null {
  return node?.pgdIdOverride || selectedPgdId;
}

export function shouldSelectOverridePgd(
  node: PGDExclusionCheckNode | null | undefined,
  anyExclusionTriggered: boolean,
): boolean {
  return Boolean(node?.pgdIdOverride && !anyExclusionTriggered);
}

/**
 * Determine whether a single pre-supply check answer triggers exclusion.
 *
 * The default behaviour (direction: "exclude_on_yes" or direction absent) is
 * that answering YES triggers exclusion — i.e. "YES, the patient HAS this
 * contraindication." This matches all existing NHS Pharmacy First PGDs.
 *
 * For AHT PGDs that phrase checks as inclusion criteria ("Does the patient
 * meet this requirement?"), direction: "exclude_on_no" means answering NO
 * triggers exclusion instead.
 */
export function isCheckTriggered(
  check: PreSupplyCheck,
  answer: "yes" | "no",
): boolean {
  const direction = check.direction ?? "exclude_on_yes";
  if (direction === "exclude_on_yes") {
    return answer === "yes";
  }
  return answer === "no";
}

/**
 * Determine whether any check in a sequential exclusion answer set has
 * triggered exclusion, respecting each check's direction.
 */
export function anyCheckTriggered(
  checks: PreSupplyCheck[],
  answers: Record<string, "yes" | "no">,
): boolean {
  return checks.some((check) => {
    const answer = answers[check.id];
    if (!answer) return false;
    return isCheckTriggered(check, answer);
  });
}
